import { useRouter } from 'next/router';
import { useState } from 'react';

import { FaceToFaceLog } from '@/constants/f2f';
import {
  HospitalSearchCommonFilter,
  HospitalSortFilter,
  getHospitalCommonFilter,
  getHospitalSearchMode,
  getHospitalSearchSortFilter,
} from '@/parsers/f2f/common';
import {
  HospitalCommonFilterType,
  HospitalSearchMode,
  HospitalSearchParamsQueries,
  HospitalSearchQueries,
  HospitalSearchSortType,
} from '@/types/hospital';
import { getCommonFilterQueryString } from '@/utils/format';
import { faceTofaceLogger, getPageName } from '@/utils/logger';
import { updateSearchParams } from '@/utils/url';

const hospitalCommonFilterTypeLogNameMap: Record<HospitalCommonFilterType, FaceToFaceLog> = {
  [HospitalCommonFilterType.Bookable]: FaceToFaceLog.click_filter_easy_booking,
  [HospitalCommonFilterType.NightOperation]: FaceToFaceLog.click_filter_night_operation,
  [HospitalCommonFilterType.Operating]: FaceToFaceLog.click_filter_operating,
  [HospitalCommonFilterType.Specialist]: FaceToFaceLog.click_filter_professional,
  [HospitalCommonFilterType.NonF2f]: FaceToFaceLog.click_filter_nonF2f,
  [HospitalCommonFilterType.SundayOperation]: FaceToFaceLog.click_filter_sunday_operation,
};

const useHospitalSearchFilters = () => {
  const router = useRouter();
  const { sortType, mode, commonFilter } = router.query as HospitalSearchQueries;
  const selectedSortFilter = getHospitalSearchSortFilter(sortType);

  const [isSortTypeBottomSheetOpen, setIsSortTypeBottomSheetOpen] = useState(false);
  const [isSearchEntireFilterBottomSheet, setIsSearchEntireFilterBottomSheet] = useState(false);

  const hospitalCommonFilter = getHospitalCommonFilter(commonFilter);

  const commonFilterCount = Object.values(hospitalCommonFilter.values).filter((value: boolean) =>
    Boolean(value)
  ).length;

  const sortFilterList: HospitalSortFilter[] = (Object.values(HospitalSearchSortType) as HospitalSearchSortType[]).map(
    (sortType) => new HospitalSortFilter(sortType)
  );

  const toggleSortTypeBottomSheet = () => {
    setIsSortTypeBottomSheetOpen((prev) => !prev);
  };

  const toggleSearchEntireFilterBottomSheet = () => {
    if (!isSearchEntireFilterBottomSheet) {
      faceTofaceLogger({
        eventName: FaceToFaceLog.click_filter_detail,
        pageName: getPageName(router),
      });
    }
    setIsSearchEntireFilterBottomSheet((prev) => !prev);
  };

  const selectHospitalSortFilter = (sortFilter: HospitalSortFilter) => {
    faceTofaceLogger({
      eventName: FaceToFaceLog.click_filter_sorting,
      pageName: getPageName(router),
      payload: {
        sortType: sortFilter.getTitle(),
      },
    });
    updateSearchParams([{ key: HospitalSearchParamsQueries.SortType, value: sortFilter.type }]);
    setIsSortTypeBottomSheetOpen(false);
  };

  const toggleHospitalFilter = (type: HospitalCommonFilterType) => () => {
    faceTofaceLogger({
      eventName: hospitalCommonFilterTypeLogNameMap[type],
      pageName: getPageName(router),
      payload: {
        status: hospitalCommonFilter.values[type] ? 'ON' : 'OFF',
      },
    });

    const currentCommonFilter = {
      ...hospitalCommonFilter.values,
      [type]: !hospitalCommonFilter.values[type],
    };

    updateSearchParams([
      {
        key: HospitalSearchParamsQueries.CommonFilter,
        value: getCommonFilterQueryString(currentCommonFilter),
      },
    ]);
  };

  const isDefaultHospitalNearMode = () => {
    return getHospitalSearchMode(mode) === HospitalSearchMode.NEAR;
  };

  const updateEntireFilter = ({
    commonFilter,
    sortFilter,
  }: {
    sortFilter: HospitalSortFilter;
    commonFilter: HospitalSearchCommonFilter;
  }) => {
    faceTofaceLogger({
      eventName: FaceToFaceLog.click_filter_detail_apply,
      pageName: getPageName(router),
      payload: {
        sortType: sortFilter.getTitle(),
        easy_booking: commonFilter.isSelected(HospitalCommonFilterType.Bookable) ? 'ON' : 'OFF',
        professional: commonFilter.isSelected(HospitalCommonFilterType.Specialist) ? 'ON' : 'OFF',
        night_operation: commonFilter.isSelected(HospitalCommonFilterType.NightOperation) ? 'ON' : 'OFF',
        operating: commonFilter.isSelected(HospitalCommonFilterType.Operating) ? 'ON' : 'OFF',
      },
    });

    updateSearchParams([
      {
        key: HospitalSearchParamsQueries.CommonFilter,
        value: getCommonFilterQueryString(commonFilter.values),
      },
      {
        key: HospitalSearchParamsQueries.SortType,
        value: sortFilter.type,
      },
    ]);
    toggleSearchEntireFilterBottomSheet();
  };

  const isChangedHospitalFilters = () => {
    if (commonFilterCount > 0) {
      return true;
    }
    return isDefaultHospitalNearMode()
      ? selectedSortFilter.type !== HospitalSearchSortType.DISTANCE
      : selectedSortFilter.type !== HospitalSearchSortType.ACCURACY;
  };

  return {
    hospitalCommonFilter,
    selectedSortFilter,
    sortFilterList,
    commonFilterCount,
    isSortTypeBottomSheetOpen,
    isDefaultHospitalNearMode,
    toggleHospitalFilter,
    selectHospitalSortFilter,
    toggleSortTypeBottomSheet,
    isSearchEntireFilterBottomSheet,
    toggleSearchEntireFilterBottomSheet,
    updateEntireFilter,
    isChangedHospitalFilters,
  };
};

export default useHospitalSearchFilters;
